import {useState, useEffect, useCallback} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import PageLoader from '../Components/PageLoader';
import TestAgentModal from '../Components/TestAgentModal';
import AgentInstructionsList from '../Components/AgentInstructionsList';

import AgentSettings from "../Components/AgentSettings";

import {AlertProvider, useAlert} from '../Components/AlertContext';
import {ConfirmationProvider} from '../Components/ConfirmationContext';
import {AgentApiProvider, useAgentApi} from '../Services/AgentApiProvider';

import {isEmpty} from '../utils/validationUtil';
import AgentAuditOperation from "../Components/AgentAuditOperation";
import {useAuth} from "../Contexts/AuthContext";
import {ArtifactApiProvider, useArtifactApi} from "../Services/ArtifactApiProvider";

function AgentContent({canEdit}) {

    const navigate = useNavigate();
    const {organizationId, agentId} = useParams();
    const {showAlert} = useAlert();
    const { status } = useAuth();
    const isDocumentPermission = status?.permissions?.includes('all:document');
    const isOrganizationsPermission = status?.permissions?.includes('all:organizations');

    const [activeTab, setActiveTab] = useState('settings');
    const [detailAudit, setDetailAudit] = useState(null);

    const {
        getAgentById,
        getAgentNotifications,
        getAgentCatalog,
        getAgentFrames,
        getAgentMarkers,
        createAgent,
        updateAgentById,
        getPrompts,
        getNotifications,
        getCatalogs,
        getFrames,
        getMarkers,
        getAgentInstructions,
        getDictionaries,
        getRelevantInstruction,
        getAllSwarm,
        getAgentSwarm,
        getDocumentCollections,
        getConnections,
    } = useAgentApi();

    const {getArtifact} = useArtifactApi();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isNew, setIsNew] = useState(true);
    const [isDisabled, setIsDisabled] = useState(true);

    const [name, setName] = useState();
    const [detailsLevel, setDetailsLevel] = useState();
    const [temperature, setTemperature] = useState(30);
    const [promptId, setPromptId] = useState();
    const [agentNotifications, setAgentNotifications] = useState([]);
    const [agentDocumentCollections, setAgentDocumentCollections] = useState([]);
    const [agentConnections, setAgentConnections] = useState([]);
    const [agentFrames, setAgentFrames] = useState([]);
    const [agentMarkers, setAgentMarkers] = useState([]);
    const [agentSwarms, setAgentSwarms] = useState([]);
    const [isActive, setIsActive] = useState(false);
    const [agentType, setAgentType] = useState();
    const [agentMemoryType, setAgentMemoryType] = useState(null);
    const [memoryLimit, setMemoryLimit] = useState(null);
    const [catalog, setCatalog] = useState();
    const [agentInstructions, setAgentInstructions] = useState([]);
    const [totalInstructions, setTotalInstructions] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [instructionsLoading, setInstructionsLoading] = useState(false);

    const [prompts, setPrompts] = useState([]);
    const [isPromptRealtime, setIsPromptRealtime] = useState(false);
    const [swarms, setSwarms] = useState([]);
    const [frames, setFrames] = useState([]);
    const [markers, setMarkers] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [documentCollections, setDocumentCollections] = useState([]);
    const [connections, setConnections] = useState([]);
    const [skills, setSkills] = useState([]);
    const [modelName, setModelName] = useState(null);
    const [reasoning, setReasoning] = useState(null);
    const [catalogs, setCatalogs] = useState([]);
    const [dictionaries, setDictionaries] = useState([]);
    const [dictionary, setDictionary] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [agentTemplates, setAgentTemplates] = useState(null);

    const [onUpdate, setOnUpdate] = useState(false);
    const [currentSearchTerm, setCurrentSearchTerm] = useState('');

    const [collectionTextWarning, setCollectionTextWarning] = useState("");
    const [disableCollections, setDisableCollections] = useState(false);

    const [auditExecution, setAuditExecution] = useState(true)

    const updateTrigger = () => setOnUpdate(prev => !prev);

    const fetchInstructions = useCallback(async (skip, limit, searchTerm = '') => {
        if (agentId && agentId !== 'new-agent') {
            setInstructionsLoading(true);
            try {
                let responseData;
                if (searchTerm) {
                    responseData = await getRelevantInstruction(agentId, searchTerm, skip, limit);
                } else {
                    responseData = await getAgentInstructions(agentId, skip, limit);
                }
                setAgentInstructions(responseData.items || []);
                setTotalInstructions(responseData.totalCount || 0);
            } catch (err) {
                if (err.response && err.response.status === 404) {
                    setAgentInstructions([]);
                    setTotalInstructions(0);
                }
            } finally {
                setInstructionsLoading(false);
            }
        }
    }, [agentId, getAgentInstructions, getRelevantInstruction, showAlert]);

    const handleSearch = useCallback((searchTerm) => {
        setCurrentSearchTerm(searchTerm);
        fetchInstructions(0, itemsPerPage, searchTerm);
    }, [fetchInstructions, itemsPerPage]);

    const handleTableChange = (skip, limit) => {
        if (limit && limit !== itemsPerPage) {
            setItemsPerPage(limit);
        }
        fetchInstructions(skip, limit);
    };

    useEffect(() => {
        if (activeTab === 'instructions' && !isNew) {
            fetchInstructions(0, itemsPerPage, currentSearchTerm);
        }
    }, [onUpdate, activeTab, isNew, itemsPerPage, currentSearchTerm]);

      useEffect(() => {
          if (agentType === "RAG") {
              if (skills?.some(s => ['SKILL:WEB_SEARCH', 'SKILL:WEB_SEARCH_HIGH'].includes(s))) {
                  setCollectionTextWarning("Web search does not support Collections");
                  setDisableCollections(true);
              } else if (reasoning === 'minimal') {
                  setCollectionTextWarning("Minimal reasoning does not support Collections");
                  setDisableCollections(true);
              } else {
                  setCollectionTextWarning("");
                  setDisableCollections(false);
              }
          }
      }, [skills, reasoning, agentDocumentCollections, agentType]);


    useEffect(() => {
        if (disableCollections) {
            setAgentDocumentCollections([]);
        }
    }, [disableCollections]);

    useEffect(() => {
      if (!promptId || !prompts?.length) {
        setIsPromptRealtime(false);
        return;
      }

      const selectedPrompt = prompts.find(p => p.id === promptId);
      setIsPromptRealtime(selectedPrompt?.pattern_type === 'realtime');
    }, [promptId, prompts]);


    useEffect(() => {
      const f = async () => {
        setLoading(true);
        try {
          if (agentId !== 'new-agent') {
            const agentPromises = [
              getAgentById(agentId).then(agent => {
                setName(agent.name);
                setDetailsLevel(agent.details_level);
                setTemperature(agent.temperature);
                setPromptId(agent.prompt_id);
                setIsActive(agent.is_active);
                setAgentType(agent.assistant_type);
                setAgentMemoryType(agent.memory_type);
                setAgentDocumentCollections(agent.document_collection_keys);
                setAgentConnections(agent.connections);
                setMemoryLimit(agent.memory_limit);
                setDictionary(agent.taxonomy_dictionary_id);
                setSkills(agent.skills);
                setModelName(agent.model_name || null);
                setReasoning(agent.reasoning || null);
                setAgentTemplates(agent.templates || [])
                setAuditExecution(agent.audit_execution)
              }),
              getAgentNotifications(agentId).then(notifications => {
                setAgentNotifications(notifications?.map(a => a.id) ?? []);
              }),
              getAgentFrames(agentId).then(frames => {
                setAgentFrames(frames?.map(a => a.id) ?? []);
              }),
              getAgentMarkers(agentId).then(markers => {
                setAgentMarkers(markers?.map(a => a.id) ?? []);
              }),
              getAgentSwarm(agentId).then(s => {
                setAgentSwarms(s?.map(s => s.key) || []);
              })
            ];

            if (isDocumentPermission) {
              agentPromises.push(
                getAgentCatalog(agentId).then(catalog => {
                  setCatalog(catalog?.id ?? null);
                })
              );
            }

            await Promise.all(agentPromises);
          }

          const globalPromises = [
            getPrompts().then(prompts => setPrompts(prompts)),
            getNotifications().then(notifications => setNotifications(notifications)),
            getDocumentCollections().then(documentCollections => setDocumentCollections(documentCollections)),
            getConnections().then(connections => setConnections(connections)),
            getFrames().then(frames => setFrames(frames)),
            getMarkers().then(markers => setMarkers(markers)),
            getDictionaries().then(dictionaries => setDictionaries(dictionaries)),
            getAllSwarm().then(s => setSwarms(s || [])),
            getArtifact(0, 1000, 'template').then(data => setTemplates(data.items))
          ];

          if (isDocumentPermission) {
            globalPromises.push(
              getCatalogs().then(catalogs => setCatalogs(catalogs))
            );
          }

          await Promise.all(globalPromises);

        } catch (error) {
          console.error('Error fetching agent data:', error);
          setError('Failed to load agent data');
        } finally {
          setLoading(false);
        }
      };

      f();
    }, [
      agentId,
      getAgentById,
      getAgentNotifications,
      getAgentCatalog,
      getAgentFrames,
      getAgentMarkers,
      getPrompts,
      getNotifications,
      getCatalogs,
      getFrames,
      getMarkers,
      getAgentSwarm,
      getDictionaries,
      isDocumentPermission,
    ]);


    useEffect(() => {
        setIsNew(agentId === 'new-agent');
        setIsDisabled(!canEdit);
    }, [agentId, canEdit]);

    const onSave = async () => {
        if (isEmpty(name)) {
            showAlert('fail', 'Please enter agent name', 'error');
            return;
        }

        if (isEmpty(detailsLevel)) {
            showAlert('fail', 'Please select level of details', 'error');
            return;
        }

        if (isEmpty(promptId)) {
            showAlert('fail', 'Please select prompt', 'error');
            return;
        }

        if (isEmpty(agentType)) {
            showAlert('fail', 'Please select agent type', 'error');
            return;
        }
        if (disableCollections && agentDocumentCollections?.length > 0) {
          showAlert('fail', `Cannot save agent: ${collectionTextWarning}`, 'error');
          return;
        }
        if (agentType === "RAG" && agentDocumentCollections?.length > 0 && isEmpty(reasoning)) {
            showAlert('fail', 'Please select reasoning', 'error');
            return;
        }

        const agent = {
            name,
            prompt_id: promptId,
            details_level: detailsLevel,
            temperature,
            notification_keys: agentNotifications,
            document_collection_keys: agentDocumentCollections,
            connections: agentConnections,
            frame_keys: agentFrames,
            marker_keys: agentMarkers,
            is_active: isActive,
            assistant_type: agentType,
            memory_type: agentMemoryType,
            memory_limit: memoryLimit,
            catalog_key: catalog,
            taxonomy_dictionary_id: dictionary,
            skills: skills,
            model_name: modelName,
            reasoning: reasoning,
            swarm: agentSwarms,
            templates: agentTemplates,
            audit_execution: auditExecution
        };

        setLoading(true);

        try {
            if (isNew) {
                const result = await createAgent(agent);
                console.log("Agent created successfully:", result);
                showAlert('Agent created successfully', 'success');
                navigate(`/organizations/${organizationId}/agents-hub/${result.key}`, { state: { organizationId } });
            } else {
                await updateAgentById(agentId, agent);
                showAlert('Agent updated successfully', 'success');
            }
        } catch (error) {
            console.error("Error saving agent data:", error);
            showAlert('Failed to save agent data', 'error');
        } finally {
            setLoading(false);
        }
    }

    const onCancel = () => {
        navigate(`/organizations/${organizationId}/agents-hub`, { state: { organizationId } });
    }

    const onChangeActiveTab = () => {
        const nextTabMap = {
            settings: "instructions",
            instructions: "auditOperation",
            auditOperation: "settings",
        };

        const nextTab = nextTabMap[activeTab] || "settings";
        setActiveTab(nextTab);

        navigate(
          `/organizations/${organizationId}/agents-hub/${agentId}?tab=${nextTab}`,
          {replace: true}
        );
    };

    if (loading) {
        return <PageLoader />;
    }

    return (
        <div className="container">
            <Breadcrumbs />
            <div className="row d-flex gap">
                <div className="radius-yes p-2 bg-yes col-6">
                    <div className='d-flex justify-between align-center'>
                        <h2 className='h2-title m-0'>{name}</h2>
                        {!isNew && (
                          <div className='buttons-container d-flex justify-end'>
                              {detailAudit && (
                                  <Button
                                      variant='primary'
                                      size="small"
                                      onClick={() => setDetailAudit(null)}
                                  >
                                      Back
                                  </Button>
                                )}

                              <Button
                                variant='secondary'
                                size='large'
                                onClick={() => {
                                    const nextTab = activeTab === 'auditOperation' ? 'settings' : 'auditOperation';
                                    setActiveTab(nextTab);
                                    navigate(
                                      `/organizations/${organizationId}/agents-hub/${agentId}?tab=${nextTab}`,
                                      {replace: true}
                                    );
                                }}
                              >
                                  {activeTab === 'auditOperation' ? 'View Settings' : 'Audit Operations'}
                              </Button>

                              <Button
                                variant='secondary'
                                size='large'
                                onClick={() => {
                                    const nextTab = activeTab === 'instructions' ? 'settings' : 'instructions';
                                    setActiveTab(nextTab);
                                    navigate(
                                      `/organizations/${organizationId}/agents-hub/${agentId}?tab=${nextTab}`,
                                      {replace: true}
                                    );
                                }}
                              >
                                  {activeTab === 'instructions' ? 'View Settings' : 'View Instructions'}
                              </Button>
                          </div>
                        )}
                    </div>
                    <div className='gap d-flex flex-row-to-col flex-column'>
                        {(activeTab === 'settings' || isNew) && (
                            <div className='int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr'>
                                <AgentSettings
                                    name={name}
                                    detailsLevel={detailsLevel}
                                    temperature={temperature}
                                    promptId={promptId}
                                    prompts={prompts}
                                    agentFrames={agentFrames}
                                    frames={frames}
                                    agentMarkers={agentMarkers}
                                    markers={markers}
                                    agentNotifications={agentNotifications}
                                    notifications={notifications}
                                    agentType={agentType}
                                    catalog={catalog}
                                    catalogs={catalogs}
                                    isActive={isActive}
                                    isDisabled={isDisabled}
                                    dictionaries={dictionaries}
                                    dictionary={dictionary}
                                    setName={setName}
                                    setDetailsLevel={setDetailsLevel}
                                    setTemperature={setTemperature}
                                    setPromptId={setPromptId}
                                    setAgentFrames={setAgentFrames}
                                    setAgentMarkers={setAgentMarkers}
                                    setAgentNotifications={setAgentNotifications}
                                    setAgentType={setAgentType}
                                    setCatalog={setCatalog}
                                    setIsActive={setIsActive}
                                    setDictionary={setDictionary}
                                    agentMemoryType={agentMemoryType}
                                    setAgentMemoryType={setAgentMemoryType}
                                    memoryLimit={memoryLimit}
                                    setMemoryLimit={setMemoryLimit}
                                    skills={skills}
                                    setSkills={setSkills}
                                    modelName={modelName}
                                    setModelName={setModelName}
                                    reasoning={reasoning}
                                    setReasoning={setReasoning}
                                    isDocumentPermission={isDocumentPermission}
                                    isOrganizationsPermission={isOrganizationsPermission}
                                    swarms={swarms}
                                    agentSwarms={agentSwarms}
                                    setAgentSwarms={setAgentSwarms}
                                    documentCollections={documentCollections}
                                    agentDocumentCollections={agentDocumentCollections}
                                    setAgentDocumentCollections={setAgentDocumentCollections}
                                    connections={connections}
                                    agentConnections={agentConnections}
                                    setAgentConnections={setAgentConnections}
                                    showAlert={showAlert}
                                    templates={templates}
                                    agentTemplates={agentTemplates}
                                    setAgentTemplates={setAgentTemplates}
                                    disableCollections={disableCollections}
                                    collectionTextWarning={collectionTextWarning}
                                    auditExecution={auditExecution}
                                    setAuditExecution={setAuditExecution}
                                />
                            </div>
                        )}
                        {activeTab === 'instructions' && !isNew && (
                            <div className='int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr'>
                                <AgentInstructionsList
                                    agentId={agentId}
                                    canEdit={!isDisabled}
                                    agentInstructions={agentInstructions}
                                    onUpdateTrigger={updateTrigger}
                                    onSearch={handleSearch}
                                    loading={instructionsLoading}
                                    totalInstructions={totalInstructions}
                                    itemsPerPage={itemsPerPage}
                                    onChangePage={handleTableChange}
                                />

                            </div>
                        )}

                        {activeTab === 'auditOperation' && !isNew && (
                            <div className='int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr'>
                                <AgentAuditOperation
                                    agentId={agentId}
                                    detailAudit={detailAudit}
                                    setDetailAudit={setDetailAudit}
                                />

                            </div>
                        )}

                    </div>

                     {(activeTab === 'settings' && (
                    <div className='buttons-container d-flex justify-end'>
                        <Button variant='primary' size='medium' disabled={isDisabled} onClick={onSave}>
                            Save
                        </Button>
                        <Button variant='danger' size='medium' onClick={onCancel}>
                            Cancel
                        </Button>
                    </div>
                     ))}
                </div>



                {!isNew && (
                    <div className="radius-yes p-2 bg-yes radius col-6">
                        <h2 className="h2-title">Test Agent</h2>
                        <div className="gap d-flex border-yes radius-yes flex-row-to-col flex-column border-yes">
                            <TestAgentModal
                                isOpen={true}
                                isPromptRealtime={isPromptRealtime}
                            />
                        </div>
                    </div>
                )}

            </div>
        </div>
    )
}

function Agent({ canEdit }) {
    return (
        <AlertProvider>
            <ConfirmationProvider>
                <AgentApiProvider>
                    <ArtifactApiProvider>
                        <AgentContent canEdit={canEdit} />
                    </ArtifactApiProvider>
                </AgentApiProvider>
            </ConfirmationProvider>
        </AlertProvider>
    );
}

export default Agent;